import {
  ChangeDetectionStrategy,
  Component,
  computed,
  effect,
  ElementRef,
  input,
  output,
  signal,
  viewChild,
} from '@angular/core';
import {
  AppSvgIconComponent,
  SignalDropdown,
  SignalTextarea,
  SignalTextField,
} from '@vallift/ngl';
import type {
  BuiltForm,
  FormInputAppearance,
  FormThemeShades,
} from '../../models/built-form';
import {
  DEFAULT_FORM_THEME_SHADES,
  FORM_THEME_PRESETS,
  type FormThemePreset,
  generateThemeShadesFromHex,
  normalizeThemeHex,
  resolveThemeShades,
  themeAccentHex,
  themePresetForShades,
} from '../../utils/form-theme-catalog';

interface FormSettingsDraft {
  title: string;
  description: string;
  coverImageUrl: string;
  inputAppearance: FormInputAppearance;
  themeColor: FormThemeShades;
}

interface SelectOption<T> {
  label: string;
  value: T;
}

const INPUT_APPEARANCES = new Set<FormInputAppearance>([
  'fill',
  'outline',
  'ghost',
  'floating',
  'ifta',
  'floatingOutline',
]);

const COVER_MAX_BYTES = 5 * 1024 * 1024;
const COVER_ACCEPT = new Set(['image/png', 'image/jpeg', 'image/webp']);

function toInputAppearance(value: unknown): FormInputAppearance {
  return INPUT_APPEARANCES.has(value as FormInputAppearance)
    ? (value as FormInputAppearance)
    : 'fill';
}

@Component({
  selector: 'app-page-config-panel',
  standalone: true,
  imports: [
    AppSvgIconComponent,
    SignalTextField,
    SignalTextarea,
    SignalDropdown,
  ],
  templateUrl: './page-config-panel.html',
  styleUrl: './page-config-panel.css',
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class PageConfigPanelComponent {
  readonly form = input<BuiltForm | null>(null);
  readonly formChange = output<BuiltForm>();

  private readonly coverFileInput =
    viewChild<ElementRef<HTMLInputElement>>('coverFileInput');

  readonly themePresets = FORM_THEME_PRESETS;
  readonly coverError = signal<string | null>(null);

  readonly inputAppearanceOptions: SelectOption<FormInputAppearance>[] = [
    { value: 'fill', label: 'Filled' },
    { value: 'outline', label: 'Outlined' },
    { value: 'ghost', label: 'Ghost' },
    { value: 'floating', label: 'Floating' },
    { value: 'ifta', label: 'IFTA' },
    { value: 'floatingOutline', label: 'Floating outlined' },
  ];

  readonly draft = signal<FormSettingsDraft>({
    title: '',
    description: '',
    coverImageUrl: '',
    inputAppearance: 'fill',
    themeColor: { ...DEFAULT_FORM_THEME_SHADES },
  });

  readonly customHex = signal(themeAccentHex(DEFAULT_FORM_THEME_SHADES));
  readonly customHexError = signal<string | null>(null);

  readonly selectedThemeLabel = computed(
    () => themePresetForShades(this.draft().themeColor).label,
  );

  readonly customHexPreview = computed(() => {
    const normalized = normalizeThemeHex(this.customHex());
    return normalized ?? '#e2e8f0';
  });

  private skipNextSync = false;
  private objectUrl: string | null = null;

  constructor() {
    effect(() => {
      const next = this.form();

      if (this.skipNextSync) {
        this.skipNextSync = false;
        return;
      }

      if (!next) {
        const themeColor = { ...DEFAULT_FORM_THEME_SHADES };
        this.draft.set({
          title: '',
          description: '',
          coverImageUrl: '',
          inputAppearance: 'fill',
          themeColor,
        });
        this.syncCustomHexFromTheme(themeColor);
        this.customHexError.set(null);
        return;
      }

      const themeColor = resolveThemeShades(next.themeColor);
      this.draft.set({
        title: next.title,
        description: next.description ?? '',
        coverImageUrl: next.coverImageUrl ?? '',
        inputAppearance: toInputAppearance(next.inputAppearance),
        themeColor,
      });
      this.syncCustomHexFromTheme(themeColor);
      this.customHexError.set(null);
    });
  }

  updateDraft<K extends keyof FormSettingsDraft>(
    key: K,
    value: FormSettingsDraft[K],
  ): void {
    this.draft.update((current) => ({ ...current, [key]: value }));
    this.emitChange();
  }

  onInputAppearanceChange(value: unknown): void {
    if (!INPUT_APPEARANCES.has(value as FormInputAppearance)) {
      return;
    }
    this.updateDraft('inputAppearance', value as FormInputAppearance);
  }

  selectTheme(preset: FormThemePreset): void {
    const themeColor = { ...preset.shades };
    this.updateDraft('themeColor', themeColor);
    this.syncCustomHexFromTheme(themeColor);
    this.customHexError.set(null);
  }

  isThemeSelected(preset: FormThemePreset): boolean {
    return themePresetForShades(this.draft().themeColor).id === preset.id;
  }

  swatchHex(preset: FormThemePreset): string {
    return preset.shades[preset.accentShade];
  }

  onCustomHexChange(value: string): void {
    this.customHex.set(value);
    if (this.customHexError()) {
      this.customHexError.set(null);
    }
  }

  applyCustomHex(): void {
    const normalized = normalizeThemeHex(this.customHex());
    if (!normalized) {
      this.customHexError.set('Enter a valid hex color (e.g. #2563eb).');
      return;
    }

    const matchingPreset = this.themePresets.find(
      (preset) => preset.shades[preset.accentShade].toLowerCase() === normalized,
    );
    if (matchingPreset) {
      this.selectTheme(matchingPreset);
      return;
    }

    const shades = generateThemeShadesFromHex(normalized);
    if (!shades) {
      this.customHexError.set('Enter a valid hex color (e.g. #2563eb).');
      return;
    }

    this.customHex.set(normalized);
    this.customHexError.set(null);
    this.updateDraft('themeColor', shades);
  }

  openCoverFilePicker(): void {
    this.coverFileInput()?.nativeElement.click();
  }

  onCoverFileSelected(event: Event): void {
    const inputEl = event.target as HTMLInputElement;
    const file = inputEl.files?.[0];
    inputEl.value = '';
    if (!file) {
      return;
    }
    this.applyCoverFile(file);
  }

  clearCover(): void {
    this.revokeObjectUrl();
    this.coverError.set(null);
    this.updateDraft('coverImageUrl', '');
  }

  private applyCoverFile(file: File): void {
    if (!COVER_ACCEPT.has(file.type)) {
      this.coverError.set('Use a PNG, JPG, or WEBP image.');
      return;
    }
    if (file.size > COVER_MAX_BYTES) {
      this.coverError.set('Image must be 5MB or smaller.');
      return;
    }

    this.coverError.set(null);
    this.revokeObjectUrl();
    const url = URL.createObjectURL(file);
    this.objectUrl = url;
    this.updateDraft('coverImageUrl', url);
  }

  private syncCustomHexFromTheme(themeColor: FormThemeShades): void {
    this.customHex.set(themeAccentHex(themeColor));
  }

  private revokeObjectUrl(): void {
    if (this.objectUrl) {
      URL.revokeObjectURL(this.objectUrl);
      this.objectUrl = null;
    }
  }

  private emitChange(): void {
    const draft = this.draft();
    const current = this.form();
    const coverImageUrl = draft.coverImageUrl.trim();

    this.skipNextSync = true;
    const next: BuiltForm = {
      title: draft.title.trim() || 'Untitled form',
      description: draft.description.trim() || undefined,
      inputAppearance: draft.inputAppearance,
      themeColor: resolveThemeShades(draft.themeColor),
      pages: current?.pages ?? [
        {
          id: 'page_1',
          order: 1,
          cta: { primaryLabel: 'Submit' },
          fields: [],
        },
      ],
    };
    if (coverImageUrl) {
      next.coverImageUrl = coverImageUrl;
    }
    this.formChange.emit(next);
  }
}
